import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, ArrowLeftIcon } from '@chakra-ui/icons';
import { Box, Flex, Select, Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';

const PAGE_SIZE_OPTIONS = [
    { name: '10', value: 10 },
    { name: '25', value: 25 },
    { name: '50', value: 50 },
    { name: '100', value: 100 },
];

function getPageList(page, totalPages) {
    const list = [];
    for (let i = 1; i <= totalPages; i++) list.push(i);
    if (page < 4) return list.slice(0, 5);
    if (page >= 4 && totalPages > 5) return list.slice(page - 3, page + 2);
    return list;
}

function Pagination({
    page = 1,
    total = 10,
    perpage = 10,
    onPageChange,
    onPerPageChange,
}) {
    const totalPages = Math.ceil(total / perpage) || 1;
    const pageList = useMemo(() => getPageList(page, totalPages), [page, totalPages]);

    const handlePageChange = (newPage) => {
        if (newPage !== page && newPage >= 1 && newPage <= totalPages) onPageChange(newPage);
    };

    const handlePerPageChange = (e) => {
        const value = Number(e.target.value);
        if (value !== perpage) onPerPageChange(value);
    };

    return (
        <>
            <Flex
                css={{
                    '@media only screen and (min-width: 500px)': { display: 'flex' },
                    '@media only screen and (max-width: 450px)': { display: 'none' },
                }}
                justifyContent="space-between"
                pl="10px"
                mt="10px"
                alignItems="center"
            >
                <Flex>
                    {page > 1 && (
                        <Flex alignItems="center">
                            <ArrowLeftIcon
                                cursor="pointer"
                                w="15px"
                                h="20px"
                                onClick={() => handlePageChange(1)}
                            />
                            <ChevronLeftIcon
                                cursor="pointer"
                                onClick={() => handlePageChange(page - 1)}
                                w="28px"
                                h="28px"
                            />
                        </Flex>
                    )}

                    {page >= 4 && totalPages > 5 && (
                        <>
                            <Box
                                transition="all 0.2s"
                                onClick={() => handlePageChange(1)}
                                cursor="pointer"
                                _hover={{ bg: 'gray.400', color: 'black' }}
                                p="5px 10px"
                                mr="5px"
                                borderRadius="2px"
                            >
                                1
                            </Box>
                            <Box transition="all 0.2s" cursor="pointer" p="5px 10px" mr="5px" borderRadius="2px">
                                ...
                            </Box>
                        </>
                    )}

                    {pageList.map((item) => (
                        <Box
                            key={item}
                            transition="all 0.2s"
                            color={page === item ? 'white' : 'black'}
                            bg={page === item ? 'gray.600' : 'white'}
                            onClick={() => handlePageChange(item)}
                            cursor="pointer"
                            _hover={{ bg: 'gray.400', color: 'black' }}
                            p="5px 10px"
                            mr="5px"
                            borderRadius="2px"
                        >
                            {item}
                        </Box>
                    ))}

                    <Flex alignItems="center">
                        {page + 3 < totalPages && totalPages > 5 ? (
                            <>
                                <Box transition="all 0.2s" cursor="pointer" p="5px 10px" mr="5px" borderRadius="2px">
                                    ...
                                </Box>
                                <Box
                                    transition="all 0.2s"
                                    onClick={() => handlePageChange(totalPages)}
                                    cursor="pointer"
                                    _hover={{ bg: 'gray.400', color: 'black' }}
                                    p="5px 10px"
                                    mr="5px"
                                    borderRadius="2px"
                                >
                                    {totalPages}
                                </Box>
                                <ChevronRightIcon
                                    color={page === totalPages ? 'gray.400' : 'black'}
                                    cursor="pointer"
                                    onClick={() => handlePageChange(page + 1)}
                                    w="28px"
                                    h="28px"
                                    mr="10px"
                                />
                                <ArrowRightIcon
                                    cursor="pointer"
                                    w="15px"
                                    h="20px"
                                    onClick={() => handlePageChange(totalPages)}
                                />
                            </>
                        ) : (
                            <>
                                {page !== totalPages && (
                                    <>
                                        <ChevronRightIcon
                                            color={page === totalPages ? 'gray.400' : 'black'}
                                            cursor="pointer"
                                            onClick={() => handlePageChange(page + 1)}
                                            w="28px"
                                            h="28px"
                                            mr="10px"
                                        />
                                        <ArrowRightIcon
                                            cursor="pointer"
                                            w="15px"
                                            h="20px"
                                            onClick={() => handlePageChange(totalPages)}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Flex>
                </Flex>
                <Flex alignItems="center">
                    <Text mr="10px">Rows per page:</Text>
                    <Select
                        onChange={handlePerPageChange}
                        value={perpage}
                        w="118px"
                        h="30px"
                    >
                        {PAGE_SIZE_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.name}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Flex>
            <Flex
                css={{
                    '@media only screen and (max-width: 450px)': { display: 'flex' },
                    '@media only screen and (min-width: 500px)': { display: 'none' },
                }}
                flexDir="column"
                pl="10px"
                mt="10px"
                alignItems="center"
                justifyContent="space-between"
            >
                <Flex>
                    {page > 1 && (
                        <Flex alignItems="center">
                            <ArrowLeftIcon
                                cursor="pointer"
                                w="15px"
                                h="20px"
                                onClick={() => handlePageChange(1)}
                            />
                            <ChevronLeftIcon
                                cursor="pointer"
                                onClick={() => handlePageChange(page - 1)}
                                w="28px"
                                h="28px"
                            />
                        </Flex>
                    )}

                    {page >= 4 && totalPages > 5 && (
                        <>
                            <Box
                                transition="all 0.2s"
                                onClick={() => handlePageChange(1)}
                                cursor="pointer"
                                _hover={{ bg: 'gray.400', color: 'black' }}
                                p="5px"
                                borderRadius="2px"
                            >
                                1
                            </Box>
                            <Box transition="all 0.2s" cursor="pointer" p="5px" borderRadius="2px">
                                ...
                            </Box>
                        </>
                    )}

                    {pageList.map((item) => (
                        <Box
                            key={item}
                            transition="all 0.2s"
                            color={page === item ? 'white' : 'black'}
                            bg={page === item ? 'gray.600' : 'white'}
                            onClick={() => handlePageChange(item)}
                            cursor="pointer"
                            _hover={{ bg: 'gray.400', color: 'black' }}
                            p="5px"
                            borderRadius="2px"
                        >
                            {item}
                        </Box>
                    ))}

                    <Flex alignItems="center">
                        {page + 3 < totalPages && totalPages > 5 ? (
                            <>
                                <Box transition="all 0.2s" cursor="pointer" p="5px" borderRadius="2px">
                                    ...
                                </Box>
                                <Box
                                    transition="all 0.2s"
                                    onClick={() => handlePageChange(totalPages)}
                                    cursor="pointer"
                                    _hover={{ bg: 'gray.400', color: 'black' }}
                                    p="5px"
                                    borderRadius="2px"
                                >
                                    {totalPages}
                                </Box>
                                <ChevronRightIcon
                                    color={page === totalPages ? 'gray.400' : 'black'}
                                    cursor="pointer"
                                    onClick={() => handlePageChange(page + 1)}
                                    w="28px"
                                    h="28px"
                                />
                                <ArrowRightIcon
                                    cursor="pointer"
                                    w="15px"
                                    h="20px"
                                    onClick={() => handlePageChange(totalPages)}
                                />
                            </>
                        ) : (
                            <>
                                {page !== totalPages && (
                                    <>
                                        <ChevronRightIcon
                                            color={page === totalPages ? 'gray.400' : 'black'}
                                            cursor="pointer"
                                            onClick={() => handlePageChange(page + 1)}
                                            w="28px"
                                            h="28px"
                                        />
                                        <ArrowRightIcon
                                            cursor="pointer"
                                            w="15px"
                                            h="20px"
                                            onClick={() => handlePageChange(totalPages)}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Flex>
                </Flex>
                <Flex alignItems="center" mt="20px">
                    <Text mr="10px">Rows per page:</Text>
                    <Select
                        onChange={handlePerPageChange}
                        value={perpage}
                        w="118px"
                        h="30px"
                    >
                        {PAGE_SIZE_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.name}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Flex>
        </>
    );
}

export default Pagination;